import { Asset } from "contentful";
import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
// @ts-ignore
import TextTruncate from "react-text-truncate";
import QualityBronze from "../../Assets/images/ui/quality/quality-bronze.png";
import QualitySilver from "../../Assets/images/ui/quality/quality-silver.png";
import QualityGold from "../../Assets/images/ui/quality/quality-gold.png";
import { AppContext, IAppInterface } from "../../context";
import LocaleNavigationLink from "../LocaleNavigationLink";
import PictureWrapper from "../PictureWrapper";
import { FavouritesSVG, TripAdvisorReviewWhiteSVG } from "../SVG";
import "./card.style.css";

interface IProps extends WithTranslation {
  title: string;
  description?: string;
  to: string;
  image: Asset;
  images?: Asset[];
  imageSlider?: boolean;
  className?: string;
  square?: boolean;
  hoverBackground?: boolean;
  imgFade?: boolean;
  subtitle?: string;
  subtitleLink?: string;
  imgBottomContent?: any;
  disabled?: boolean;
  imgTopContent?: any;
  aboveContent?: any;
  favourite?: string;
  tripAdvisorRating?: number;
  tripAdvisorReviewCount?: number;
  onClick?: () => void;
  onFavourite?: (value: boolean) => void;
  tripAdvisorPosition?: "top" | "bottom";
  quality?: string;
  numLines?: number;
}

const Card = (props: IProps) => {
  const {
    title,
    description,
    image,
    images,
    imageSlider = false,
    className = "",
    to,
    square = false,
    hoverBackground = true,
    imgFade = false,
    subtitle = false,
    imgBottomContent = false,
    disabled = false,
    imgTopContent = false,
    aboveContent = false,
    favourite,
    tripAdvisorRating,
    tripAdvisorReviewCount,
    onFavourite,
    onClick,
    tripAdvisorPosition = "top",
    quality,
  } = props;
  const holderRef = React.useRef(null);
  const [goLeft, setGoLeft] = React.useState(0);
  const [haveRight, setHaveRight] = React.useState(true);

  React.useEffect(() => {
    const holder = holderRef.current as unknown as HTMLDivElement;
    if (holder) {
      const lastElement = holder.children[
        holder.children.length - 1
      ] as unknown as HTMLDivElement;
      if (lastElement) {
        const elementsPerView = Math.floor(
          holder.offsetWidth / lastElement.offsetWidth
        );
        setHaveRight(elementsPerView + goLeft < holder.children.length);
      }
    }
  }, [goLeft]);

  const moveSlider = (
    e: React.MouseEvent<HTMLDivElement>,
    moveLeft: boolean
  ) => {
    e.preventDefault();
    const holder = holderRef.current as unknown as HTMLDivElement;
    if (holder) {
      const lastElement = holder.children[
        holder.children.length - 1
      ] as unknown as HTMLDivElement;
      if (lastElement) {
        const fullWidth = lastElement.offsetLeft + lastElement.offsetWidth;
        // const startHidden = fullWidth - holder.offsetWidth;
        const elementsPerView = Math.floor(
          holder.offsetWidth / lastElement.offsetWidth
        );
        setHaveRight(elementsPerView + goLeft + 1 < holder.children.length);

        if (moveLeft && elementsPerView + goLeft < holder.children.length) {
          holder.children[elementsPerView + goLeft].scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "end",
          });
          setGoLeft(goLeft + 1);
        }
        if (!moveLeft && goLeft > 0) {
          holder.children[goLeft - 1].scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "start",
          });
          setGoLeft(goLeft - 1);
        }
      }
    }
  };

  return (
    <AppContext.Consumer>
      {({ addFavourite, removeFavourite, favouritesList }: IAppInterface) => {
        const isFavourite = favourite && favouritesList.includes(favourite);
        return (
          <LocaleNavigationLink
            className={`card ${hoverBackground ? "-hover-background" : ""} ${
              square ? "-square" : ""
            } ${disabled ? "-disabled" : ""} ${className}`}
            to={to}
            onClick={onClick}
          >
            <div className="card__above-content">{aboveContent}</div>
            <div
              className={`card__img-container ${
                imageSlider && images && images.length > 0 ? "-hasSlider" : ""
              }`}
            >
              {imageSlider && images && images.length > 0 ? (
                <div className="cardslider">
                  <div className="cardslider__holder" ref={holderRef}>
                    {images.map((item, i) => (
                      <div key={i}>
                        <PictureWrapper
                          className="card__img"
                          small={square ? [250, 250] : [620, 435]}
                          medium={square ? [250, 250] : [380, 264]}
                          large={square ? [250, 250] : [350, 245]}
                          asset={item}
                          options={[["fit", "fill"]]}
                        />
                      </div>
                    ))}
                  </div>
                  <div
                    className={`cardslider__prev -slider ${
                      goLeft <= 0 ? "inactive" : ""
                    } -blue -inside`}
                    onClick={(e) => moveSlider(e, false)}
                  ></div>
                  <div
                    className={`cardslider__next -slider ${
                      haveRight ? "" : "inactive"
                    } -blue -inside `}
                    onClick={(e) => moveSlider(e, true)}
                  ></div>
                </div>
              ) : (
                <PictureWrapper
                  className="card__img"
                  small={square ? [250, 250] : [620, 435]}
                  medium={square ? [250, 250] : [380, 264]}
                  large={square ? [250, 250] : [350, 245]}
                  asset={image}
                  options={[["fit", "fill"]]}
                />
              )}

              <div className={`card__img-top-content`}>{imgTopContent}</div>
              <div
                className={`card__img-bottom-content ${
                  imgFade ||
                  (tripAdvisorRating && tripAdvisorPosition === "top")
                    ? "-fade"
                    : ""
                }`}
              >
                {imgBottomContent}
                {tripAdvisorRating && tripAdvisorPosition === "top" && (
                  <div className="card__card-rating-top">
                    <TripAdvisorReviewWhiteSVG
                      count={tripAdvisorRating}
                      className="card__trip-advisor-svg"
                    />
                    {tripAdvisorReviewCount &&
                      props.i18n.t("tripAdvisorReviews", {
                        value: tripAdvisorReviewCount,
                      })}
                  </div>
                )}
              </div>
            </div>
            <div className="card__caption">
              <h4 className="card__title">{title}</h4>
              {subtitle && <h6 className="card__subtitle">{subtitle}</h6>}
              {description && (
                <TextTruncate
                  line={props.numLines || 2}
                  text={description}
                  truncateText="…"
                  className="body--2 card__description"
                />
              )}
            </div>
            {favourite && (
              <FavouritesSVG
                className="card__favourites"
                onClick={(ev: any) => {
                  if (onFavourite) {
                    onFavourite(!isFavourite);
                  }
                  if (isFavourite) {
                    removeFavourite(favourite);
                  } else {
                    addFavourite(favourite, "/" + props.i18n.language + to);
                  }
                  ev.stopPropagation();
                  ev.preventDefault();
                }}
                style={isFavourite ? "active" : "grey"}
              />
            )}
            {quality && quality !== "no" && (
              <div className="card__quality">
                <div
                  onClick={(e) => {
                    /*
                    e.preventDefault();
                    window.open(
                      "https://www.curacaotouristboard.com/island-developments/quality-assurance/",
                      "_blank",
                      "noopener noreferrer"
                    );
                    */
                  }}
                  className="card__quality-icon"
                >
                  <img
                    src={
                      quality === "Gold"
                        ? QualityGold
                        : quality === "Silver"
                        ? QualitySilver
                        : QualityBronze
                    }
                    className="-normal"
                    alt="Quality Assurance Approved"
                  />
                  {/*
                  <span>
                    {props.i18n.t("qualityAssurance")}{" "}
                    {quality === "Yes" ? props.i18n.t("approved") : quality}
                  </span>
                  */}
                </div>
              </div>
            )}
            {tripAdvisorRating && tripAdvisorPosition === "bottom" && (
              <>
                <div className="card__card-rating-bottom">
                  <img
                    className="card__card-rating"
                    alt={`Trip advisor rating ${tripAdvisorRating.toFixed(1)}`}
                    src={`https://www.tripadvisor.com/img/cdsi/img2/ratings/traveler/${tripAdvisorRating.toFixed(
                      1
                    )}-MCID-5.svg`}
                  />
                  {tripAdvisorReviewCount &&
                    tripAdvisorReviewCount + " " + props.i18n.t("reviews")}
                </div>
              </>
            )}
          </LocaleNavigationLink>
        );
      }}
    </AppContext.Consumer>
  );
};

export default withTranslation()(Card);
